import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  BadgeCheck,
  ChevronRight,
  Inbox,
  Mail,
  MapPin,
  Phone,
  Route as RouteIcon,
  StickyNote,
} from "lucide-react";
import type { ReactNode } from "react";
import { LEAD_STATUS_LABEL, LEAD_TEMPERATURE_LABEL } from "@rn/contracts";
import { PageBody } from "@/components/crm/AppShell";
import { ContactActions, CopyButton } from "@/components/crm/ContactActions";
import {
  LeadSourceBadge,
  LeadStatusBadge,
  LeadTemperatureBadge,
} from "@/components/crm/LeadControls";
import { CustomFieldsForm, type FieldValue } from "@/components/crm/CustomFieldsForm";
import { groupByDay, TimelineDay, TimelineItem } from "@/components/crm/LeadTimeline";
import { SaleButton } from "@/components/crm/SaleDialog";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { formatCurrency, formatDateTime, formatPhone, horaCurta, sourceLabel } from "@/lib/crm";
import { tagClass } from "@/lib/tag-colors";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/contatos/$contactId")({
  component: ContatoPage,
});

type ContactDetail = {
  id: string;
  fullName: string | null;
  phone: string | null;
  phoneNormalized: string | null;
  email: string | null;
  city: string | null;
  state: string | null;
  isCustomer: boolean;
  becameCustomerAt: string | null;
  lifetimeValue: number;
  consentAt: string | null;
  createdAt: string;
  firstTouch: {
    platform: string | null;
    source: string | null;
    campaignName: string | null;
    occurredAt: string;
  } | null;
  leads: Array<{
    id: string;
    status: keyof typeof LEAD_STATUS_LABEL;
    temperature: keyof typeof LEAD_TEMPERATURE_LABEL;
    source: string | null;
    createdAt: string;
    firstResponseAt: string | null;
    lostReason: string | null;
    owner: { id: string; fullName: string | null } | null;
  }>;
  deals: Array<{
    id: string;
    title: string;
    status: string;
    value: string | null;
    stageName: string | null;
    createdAt: string;
  }>;
  journey: Array<{
    id: string;
    position: number;
    channel: string | null;
    campaignName: string | null;
    source: string | null;
    occurredAt: string;
  }>;
  notes: Array<{
    id: string;
    body: string;
    createdAt: string;
    author: { id: string; fullName: string | null } | null;
  }>;
  tags: Array<{ id: string; name: string; color: string | null }>;
  customFields: FieldValue[];
};

/**
 * Ficha do contato.
 *
 * O que esta tela responde e a do lead não: quantas vezes esta pessoa voltou,
 * por quais caminhos, e o que já comprou. A ficha do lead mostra UMA captação;
 * aqui está a história inteira.
 *
 * A coluna principal são as captações (cada uma abre a ficha do lead) e as
 * anotações de todas elas; a lateral, o que descreve a pessoa. No celular a
 * lateral vai para baixo: quem abre o contato quer achar a captação certa.
 */
function ContatoPage() {
  const { contactId } = Route.useParams();
  const { data: contact, isLoading } = useQuery({
    queryKey: ["contact", contactId],
    queryFn: () => api.get<ContactDetail>(`/contacts/${contactId}`),
  });

  if (isLoading) return <FichaCarregando />;
  if (!contact) return null;

  const nome = contact.fullName ?? "Sem nome";
  const telefone = contact.phoneNormalized ?? contact.phone;
  const cidade = contact.city
    ? `${contact.city}${contact.state ? `/${contact.state}` : ""}`
    : contact.state;
  const dias = groupByDay(contact.notes, (n) => new Date(n.createdAt).getTime());

  return (
    <PageBody className="space-y-5">
      <Link
        to="/contatos"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        Contatos
      </Link>

      {/* ── Cabeçalho: quem é, o que já é para a clínica, o que dá para fazer ─ */}
      <Card className="overflow-hidden">
        <div className="flex flex-col gap-5 p-5 sm:p-6 md:flex-row md:items-start">
          <Avatar name={nome} seed={telefone ?? contact.id} size="xl" className="shrink-0" />

          <div className="min-w-0 flex-1 space-y-3">
            <div>
              <h1 className="font-display text-xl font-semibold leading-tight tracking-tight sm:text-2xl">
                {nome}
              </h1>
              {(contact.isCustomer || contact.tags.length > 0) && (
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  {contact.isCustomer && (
                    <Badge variant="success">
                      <BadgeCheck />
                      Cliente
                    </Badge>
                  )}
                  {/* Etiquetas vêm das captações; aqui só se lê. Para mudar,
                      abre-se o lead que as recebeu. */}
                  {contact.tags.map((tag) => (
                    <span
                      key={tag.id}
                      className={cn(
                        "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold leading-4",
                        tagClass(tag.color),
                      )}
                    >
                      {tag.name}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm">
              {telefone && (
                <span className="inline-flex items-center gap-1 tabular-nums">
                  <Phone className="size-3.5 text-muted-foreground" />
                  {formatPhone(telefone)}
                  <CopyButton value={telefone} label="telefone" />
                </span>
              )}
              {contact.email && (
                <span className="inline-flex min-w-0 items-center gap-1">
                  <Mail className="size-3.5 shrink-0 text-muted-foreground" />
                  <span className="truncate">{contact.email}</span>
                  <CopyButton value={contact.email} label="e-mail" />
                </span>
              )}
              {cidade && (
                <span className="inline-flex items-center gap-1 text-muted-foreground">
                  <MapPin className="size-3.5" />
                  {cidade}
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 md:shrink-0 md:justify-end">
            <ContactActions contactId={contact.id} phone={telefone} />
            <SaleButton contactId={contact.id} contactName={contact.fullName} />
          </div>
        </div>

        {/* Três números que resumem a relação, sempre à vista. */}
        <div className="grid divide-y border-t bg-muted/30 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
          <Fato
            label="Captações"
            value={contact.leads.length}
            hint={contact.leads.length === 1 ? "uma vez na base" : "vezes que chegou"}
          />
          <Fato
            label="Valor gerado"
            value={contact.lifetimeValue > 0 ? formatCurrency(contact.lifetimeValue) : "—"}
            hint={contact.lifetimeValue > 0 ? "em vendas pagas" : "nenhuma venda paga"}
          />
          <Fato
            label={contact.isCustomer ? "Cliente desde" : "Na base desde"}
            value={formatDateTime(
              contact.isCustomer && contact.becameCustomerAt
                ? contact.becameCustomerAt
                : contact.createdAt,
            )}
            hint={
              contact.isCustomer
                ? `na base desde ${formatDateTime(contact.createdAt)}`
                : "ainda não comprou"
            }
          />
        </div>
      </Card>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_20rem] lg:gap-6">
        {/* ── Principal: as captações e o que foi anotado nelas ────────────── */}
        <section className="min-w-0 space-y-5">
          <Bloco
            title={`Captações (${contact.leads.length})`}
            description="Cada linha é uma vez que esta pessoa chegou. Abra para ver o atendimento daquela vez."
            flush
          >
            {contact.leads.length === 0 ? (
              <EmptyState compact icon={Inbox} title="Nenhuma captação." />
            ) : (
              <ul className="divide-y">
                {contact.leads.map((lead) => (
                  <li key={lead.id}>
                    <Link
                      to="/leads/$leadId"
                      params={{ leadId: lead.id }}
                      className="flex items-center gap-3 px-5 py-3 transition-colors hover:bg-muted/40 active:bg-muted/60"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <LeadSourceBadge source={lead.source} />
                          <LeadStatusBadge status={lead.status} />
                          <LeadTemperatureBadge temperature={lead.temperature} />
                        </div>
                        <p className="mt-1.5 text-xs text-muted-foreground">
                          {formatDateTime(lead.createdAt)}
                          <span className="mx-1 text-border">·</span>
                          {lead.owner?.fullName ?? (
                            <span className="text-warning-foreground dark:text-warning">
                              sem responsável
                            </span>
                          )}
                          {lead.lostReason && (
                            <>
                              <span className="mx-1 text-border">·</span>
                              <span className="text-destructive">{lead.lostReason}</span>
                            </>
                          )}
                        </p>
                      </div>
                      <ChevronRight className="size-4 shrink-0 text-muted-foreground/60" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Bloco>

          {contact.deals.length > 0 && (
            <Bloco title="Oportunidades" flush>
              <ul className="divide-y">
                {contact.deals.map((deal) => (
                  <li
                    key={deal.id}
                    className="flex flex-wrap items-center gap-x-3 gap-y-1 px-5 py-3"
                  >
                    <span className="min-w-0 flex-1 truncate text-sm font-medium">
                      {deal.title}
                    </span>
                    {deal.stageName && <Badge variant="outline">{deal.stageName}</Badge>}
                    <span className="text-xs tabular-nums text-muted-foreground">
                      {deal.value ? formatCurrency(deal.value) : "sem valor"}
                      <span className="mx-1 text-border">·</span>
                      {formatDateTime(deal.createdAt)}
                    </span>
                  </li>
                ))}
              </ul>
            </Bloco>
          )}

          <Bloco title="Anotações" description="De todas as captações desta pessoa.">
            {dias.length === 0 ? (
              <EmptyState
                compact
                icon={StickyNote}
                title="Nenhuma anotação."
                description="As notas são escritas na ficha de cada lead e aparecem aqui reunidas."
              />
            ) : (
              <div className="space-y-6">
                {dias.map((dia) => (
                  <TimelineDay key={dia.key} label={dia.label}>
                    {dia.items.map((note) => (
                      <TimelineItem
                        key={note.id}
                        icon={StickyNote}
                        tone="note"
                        body={note.body}
                        meta={
                          <>
                            {horaCurta(note.createdAt)}
                            {note.author?.fullName ? ` · ${note.author.fullName}` : ""}
                          </>
                        }
                      />
                    ))}
                  </TimelineDay>
                ))}
              </div>
            )}
          </Bloco>
        </section>

        {/* ── Lateral: o que descreve a pessoa ─────────────────────────────── */}
        <aside className="min-w-0 space-y-5">
          <Bloco
            title="Informações"
            description="Ficam na pessoa e seguem para os próximos leads dela."
          >
            <CustomFieldsForm
              path={`/contacts/${contact.id}`}
              fields={contact.customFields}
              invalidateKeys={[["contact", contact.id]]}
            />
          </Bloco>

          <Bloco title="Relacionamento">
            <dl className="space-y-2.5">
              <Item label="Na base desde" value={formatDateTime(contact.createdAt)} />
              <Item label="Captações" value={String(contact.leads.length)} />
              <Item
                label="Virou cliente em"
                value={
                  contact.becameCustomerAt ? formatDateTime(contact.becameCustomerAt) : "ainda não"
                }
              />
              <Item
                label="Valor gerado"
                value={contact.lifetimeValue > 0 ? formatCurrency(contact.lifetimeValue) : "—"}
              />
              <Item
                label="Consentimento"
                value={contact.consentAt ? formatDateTime(contact.consentAt) : "não registrado"}
              />
            </dl>
          </Bloco>

          <Bloco
            title="Primeira origem"
            description="De onde esta pessoa veio na primeira vez. Não muda nunca, mesmo que ela volte por outra campanha."
          >
            <dl className="space-y-2.5">
              <Item label="Plataforma" value={contact.firstTouch?.platform} />
              <Item label="Origem" value={sourceLabel(contact.firstTouch?.source)} />
              <Item label="Campanha" value={contact.firstTouch?.campaignName} />
              <Item label="Quando" value={formatDateTime(contact.firstTouch?.occurredAt)} />
            </dl>
          </Bloco>

          <Bloco title="Jornada" description="Os toques desta pessoa, na ordem em que aconteceram.">
            {contact.journey.length === 0 ? (
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <RouteIcon className="size-4" />
                Nenhum toque registrado.
              </p>
            ) : (
              <ol className="space-y-3">
                {contact.journey.map((tp) => (
                  <li key={tp.id} className="flex gap-3 text-sm">
                    <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-semibold tabular-nums text-muted-foreground">
                      {tp.position}
                    </span>
                    <div className="min-w-0">
                      <p className="truncate font-medium">
                        {tp.campaignName ?? sourceLabel(tp.source)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {tp.channel ? `${tp.channel} · ` : ""}
                        {formatDateTime(tp.occurredAt)}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </Bloco>
        </aside>
      </div>
    </PageBody>
  );
}

/** Uma célula da faixa de números do cabeçalho. */
function Fato({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <div className="px-5 py-3 sm:px-6">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className="kpi mt-0.5 text-lg font-semibold leading-tight">{value}</p>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

/** Cartão de bloco: título, contexto opcional e conteúdo (sem margem quando é lista). */
function Bloco({
  title,
  description,
  flush,
  children,
}: {
  title: string;
  description?: string;
  /** Lista que encosta nas bordas do cartão. */
  flush?: boolean;
  children: ReactNode;
}) {
  return (
    <Card className={cn(flush && "overflow-hidden")}>
      <CardHeader className={cn("flex-col gap-0", flush && "pb-4")}>
        <CardTitle>{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent className={cn(flush && "border-t p-0")}>{children}</CardContent>
    </Card>
  );
}

function Item({ label, value }: { label: string; value: ReactNode | null | undefined }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="break-words text-sm">{value || "—"}</dd>
    </div>
  );
}

function FichaCarregando() {
  return (
    <PageBody className="space-y-5" aria-busy>
      <Skeleton className="h-4 w-16" />
      <Skeleton className="h-64 rounded-xl sm:h-52" />
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_20rem] lg:gap-6">
        <div className="space-y-5">
          <Skeleton className="h-48 rounded-xl" />
          <Skeleton className="h-40 rounded-xl" />
        </div>
        <div className="space-y-5">
          <Skeleton className="h-32 rounded-xl" />
          <Skeleton className="h-40 rounded-xl" />
        </div>
      </div>
    </PageBody>
  );
}
